import { useState, useEffect, useCallback } from "react";
import { FileText, Heart, Users, DollarSign, BookOpen, Megaphone, HandHeart, Printer, Download, ArrowLeft, Plus, Pencil, Trash2, Save, X, Loader2, GripVertical, ChevronUp, ChevronDown, RotateCcw } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import axios from "axios";
import { DocumentRenderer } from "../DocumentRenderer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  "file-text": FileText,
  "heart": Heart,
  "users": Users,
  "dollar-sign": DollarSign,
  "book-open": BookOpen,
  "megaphone": Megaphone,
  "hand-heart": HandHeart,
};

const COLOR_MAP = {
  warm: "bg-warm-100 text-warm-700",
  rose: "bg-rose-100 text-rose-700",
  blue: "bg-blue-100 text-blue-700",
  red: "bg-red-100 text-red-700",
  amber: "bg-amber-100 text-amber-700",
  purple: "bg-purple-100 text-purple-700",
  sage: "bg-sage-100 text-sage-700",
  green: "bg-green-100 text-green-700",
  orange: "bg-orange-100 text-orange-700",
};

/* ========== DOCUMENT EDITOR ========== */
const DocumentEditor = ({ doc, onSave, onCancel, getAuthHeader }) => {
  const [form, setForm] = useState({
    title: doc?.title || "",
    description: doc?.description || "",
    category: doc?.category || "Grant & Partner Materials",
    icon: doc?.icon || "file-text",
    color: doc?.color || "warm",
    header_title: doc?.header_title || "",
    header_subtitle: doc?.header_subtitle || "",
    sections: doc?.sections || [{ heading: "", content: "" }],
  });
  const [saving, setSaving] = useState(false);

  const updateSection = (idx, field, value) => {
    const updated = [...form.sections];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, sections: updated });
  };

  const addSection = () => {
    setForm({ ...form, sections: [...form.sections, { heading: "", content: "" }] });
  };

  const removeSection = (idx) => {
    if (form.sections.length <= 1) return;
    setForm({ ...form, sections: form.sections.filter((_, i) => i !== idx) });
  };

  const moveSection = (idx, dir) => {
    const arr = [...form.sections];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    setForm({ ...form, sections: arr });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (doc?.id) {
        await axios.put(`${API}/admin/documents/${doc.id}`, form, getAuthHeader());
      } else {
        await axios.post(`${API}/admin/documents`, form, getAuthHeader());
      }
      toast.success("Document saved");
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="document-editor">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
          {doc?.id ? "Edit Document" : "New Document"}
        </h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}><X className="w-4 h-4 mr-1" /> Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-warm-700 hover:bg-warm-800 text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
          </Button>
        </div>
      </div>

      {/* Metadata */}
      <div className="grid md:grid-cols-2 gap-4 bg-gray-50 rounded-xl p-4">
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase">Title</label>
          <input className="w-full border rounded-lg px-3 py-2 mt-1 text-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase">Category</label>
          <select className="w-full border rounded-lg px-3 py-2 mt-1 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            <option>Grant & Partner Materials</option>
            <option>Fundraising</option>
            <option>Marketing Flyers</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase">Description</label>
          <input className="w-full border rounded-lg px-3 py-2 mt-1 text-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Icon</label>
            <select className="w-full border rounded-lg px-3 py-2 mt-1 text-sm" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })}>
              {Object.keys(ICON_MAP).map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Color</label>
            <select className="w-full border rounded-lg px-3 py-2 mt-1 text-sm" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}>
              {Object.keys(COLOR_MAP).map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="bg-gray-50 rounded-xl p-4">
        <label className="text-xs font-medium text-gray-500 uppercase block mb-2">Document Header</label>
        <div className="grid md:grid-cols-2 gap-3">
          <input className="border rounded-lg px-3 py-2 text-sm" placeholder="Header Title" value={form.header_title} onChange={(e) => setForm({ ...form, header_title: e.target.value })} />
          <input className="border rounded-lg px-3 py-2 text-sm" placeholder="Header Subtitle" value={form.header_subtitle} onChange={(e) => setForm({ ...form, header_subtitle: e.target.value })} />
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-gray-500 uppercase">Sections ({form.sections.length})</label>
          <Button size="sm" variant="outline" onClick={addSection} className="text-xs"><Plus className="w-3 h-3 mr-1" /> Add Section</Button>
        </div>
        {form.sections.map((section, idx) => (
          <div key={idx} className="border border-gray-200 rounded-xl p-4 bg-white">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <GripVertical className="w-4 h-4 text-gray-300" />
                <span className="text-xs text-gray-400 font-medium">Section {idx + 1}</span>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => moveSection(idx, 1)} disabled={idx === form.sections.length - 1} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                <button onClick={() => removeSection(idx)} className="p-1 text-red-400 hover:text-red-600 ml-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm font-semibold mb-2"
              placeholder="Section Heading"
              value={section.heading}
              onChange={(e) => updateSection(idx, "heading", e.target.value)}
            />
            <textarea
              className="w-full border rounded-lg px-3 py-2 text-sm min-h-[120px] leading-relaxed"
              placeholder="Section content. Use bullet points with •, numbered lists with 1. 2. 3., and blank lines for paragraphs."
              value={section.content}
              onChange={(e) => updateSection(idx, "content", e.target.value)}
            />
          </div>
        ))}
      </div>

      <div className="text-xs text-gray-400 bg-gray-50 rounded-lg p-3">
        <strong>Formatting tips:</strong> Start lines with <code className="bg-gray-200 px-1 rounded">•</code> for bullet points, <code className="bg-gray-200 px-1 rounded">1.</code> for numbered lists. ALL CAPS lines become bold headers. Blank lines create paragraph breaks.
      </div>
    </div>
  );
};

/* ========== MAIN PANEL ========== */
export const AdminDocumentsPanel = ({ getAuthHeader }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDoc, setActiveDoc] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | doc object
  const [seeding, setSeeding] = useState(false);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/documents`, getAuthHeader());
      setDocuments(res.data);
    } catch {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handlePrint = () => window.print();

  const handleDownloadPdf = async (docId, title) => {
    try {
      const res = await axios.get(`${API}/admin/documents/${docId}/pdf`, {
        ...getAuthHeader(),
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const slug = (title || docId).toLowerCase().replace(/[^a-z0-9._-]+/g, "-").replace(/^-|-$/g, "");
      a.download = `${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download PDF");
    }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm("Delete this document permanently?")) return;
    try {
      await axios.delete(`${API}/admin/documents/${id}`, getAuthHeader());
      toast.success("Document deleted");
      fetchDocs();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("This will replace ALL documents with the defaults. Are you sure?")) return;
    setSeeding(true);
    try {
      await axios.post(`${API}/admin/documents/seed`, {}, getAuthHeader());
      toast.success("Documents reset to defaults");
      fetchDocs();
    } catch {
      toast.error("Failed to reset");
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-warm-600" /></div>;
  }

  // Editor view
  if (editing !== null) {
    return (
      <DocumentEditor
        doc={editing === "new" ? null : editing}
        getAuthHeader={getAuthHeader}
        onCancel={() => setEditing(null)}
        onSave={() => { setEditing(null); fetchDocs(); }}
      />
    );
  }

  // Document preview view
  if (activeDoc) {
    const doc = documents.find((d) => d.id === activeDoc);
    return (
      <div>
        <div className="print-hide mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => setActiveDoc(null)} data-testid="doc-back-btn" className="gap-2">
              <ArrowLeft className="w-4 h-4" /> Back to Documents
            </Button>
            <h3 className="font-semibold text-gray-900" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>{doc?.title}</h3>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(doc)} className="gap-1">
              <Pencil className="w-4 h-4" /> Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDownloadPdf(doc.id, doc.title)}
              data-testid="doc-download-pdf-btn"
              className="gap-1"
            >
              <Download className="w-4 h-4" /> Download PDF
            </Button>
            <Button onClick={handlePrint} data-testid="doc-print-btn" className="bg-warm-700 hover:bg-warm-800 text-white gap-2">
              <Printer className="w-4 h-4" /> Print / Save as PDF
            </Button>
          </div>
        </div>
        <div className="doc-print-area overflow-x-auto">
          <DocumentRenderer doc={doc} />
        </div>
      </div>
    );
  }

  // Document list view
  const categories = [...new Set(documents.map((d) => d.category))];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500">
          Edit documents directly or click to preview. Print / Save as PDF from the preview.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={resetDefaults} disabled={seeding} className="text-xs gap-1">
            <RotateCcw className="w-3 h-3" /> {seeding ? "Resetting..." : "Reset Defaults"}
          </Button>
          <Button size="sm" onClick={() => setEditing("new")} className="bg-warm-700 hover:bg-warm-800 text-white gap-1" data-testid="new-document-btn">
            <Plus className="w-4 h-4" /> New Document
          </Button>
        </div>
      </div>

      {categories.map((cat) => (
        <div key={cat} className="mb-8">
          <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>{cat}</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documents.filter((d) => d.category === cat).map((doc) => {
              const IconComp = ICON_MAP[doc.icon] || FileText;
              const colorCls = COLOR_MAP[doc.color] || COLOR_MAP.warm;
              return (
                <div
                  key={doc.id}
                  className="border border-gray-100 rounded-xl p-5 hover:shadow-md transition-shadow cursor-pointer group relative"
                  onClick={() => setActiveDoc(doc.id)}
                  data-testid={`doc-card-${doc.id}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${colorCls}`}>
                    <IconComp className="w-5 h-5" />
                  </div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>{doc.title}</h4>
                  <p className="text-xs text-gray-500 line-clamp-2">{doc.description}</p>
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => handleDownloadPdf(doc.id, doc.title)}
                      title="Download PDF"
                      data-testid={`doc-card-download-${doc.id}`}
                      className="p-1.5 rounded-lg bg-white border border-gray-200 text-gray-500 hover:text-warm-700 hover:border-warm-300"
                    >
                      <Download className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => setEditing(doc)}
                      title="Edit"
                      className="p-1.5 rounded-lg bg-white border border-gray-200 text-gray-500 hover:text-warm-700 hover:border-warm-300"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => deleteDoc(doc.id)}
                      title="Delete"
                      className="p-1.5 rounded-lg bg-white border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-300"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
